/**
 * Modals - Classic layout modals section
 *
 * Renders all modal components for the page.
 * Shared between ClassicLayout and AttractionLayout.
 */
import { useRouter } from "next/router";
import { useTranslation } from "@shared/utils/internationalization";
import MembershipTypesList from "@shared/modules/memberships/components/MembershipTypesList/MembershipTypesList";
import TicketTypesList from "@shared/modules/events/components/tickets/TicketTypesList/TicketTypesList.web";
import ContactDetailsModalSelection from "@shared/modules/seating/components/contactDetailsModal/ContactDetailsModalSelection";
import { SubOptionsProvider } from "@shared/contexts/SubOptionsContext/SubOptionsContext";
import type { PageData } from "../../../adapters/types";
import { DetailPageModal } from "../../../components/modals/DetailPageModal.web";
import { MapModal } from "../../../components/maps/MapModal.web";
import ProductCategoriesList from "../../../components/products/ProductCategoriesList.web";
import { ShareModal } from "../../../components/shared/ShareModal.web";

interface ModalsProps {
  data: PageData;
}

export function Modals({ data }: ModalsProps): JSX.Element {
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { modals, mainContent, rawData, pageType, getModalSubtitle, header } =
    data;

  // Build share URL from current page
  const shareUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `${process.env.NEXT_PUBLIC_APP_URL || ""}${router.asPath}`;

  // Check if we have valid location data for the map modal
  const hasMapData =
    mainContent.locationInfo?.latLng && !mainContent.locationInfo?.virtualType;

  return (
    <>
      {/* Share modal */}
      <ShareModal
        isOpen={modals.share.isOpen}
        onClose={modals.share.onClose}
        title={header.title}
        subtitle={header.subtitle}
        organizer={header.organizer.name}
        image={header.image}
        url={shareUrl}
      />
      {/* Map modal - triggered by location badge click */}
      {hasMapData && mainContent.locationInfo && (
        <MapModal
          isOpen={rawData.viewMap ?? false}
          onClose={() => rawData.setViewMap?.(false)}
          latLng={mainContent.locationInfo.latLng ?? ""}
          locationName={mainContent.locationInfo.locationName}
          address={mainContent.locationInfo.address}
        />
      )}

      {/* Contact details modal */}
      <ContactDetailsModalSelection
        venueId={mainContent.venueInfo.id}
        isOpen={modals.contact.isOpen}
        onClose={modals.contact.onClose}
        user={rawData.user}
        isLoggedIn={rawData.isLoggedIn ?? false}
        refundPolicyText={mainContent.refundPolicy}
      />

      {/* Event-specific modals */}
      {pageType === "event" && modals.tickets && (
        <>
          {/* Ticket types modal */}
          <SubOptionsProvider>
            <DetailPageModal
              title={t("Tickets")}
              subtitle={getModalSubtitle?.() ?? ""}
              isOpen={modals.tickets.isOpen}
              onClose={modals.tickets.onClose}
              venue={rawData.event?.venue}
              itemListElement={
                <TicketTypesList
                  eventData={rawData.event}
                  isLoading={data.ui.isLoading}
                  ticketsAuthorizedByPassword={
                    rawData.ticketsAuthorizedByPassword ?? undefined
                  }
                  basketVoucherCode={rawData.voucherData?.basketVoucherCode}
                  userVouchers={rawData.voucherData?.userVouchers}
                />
              }
            />
          </SubOptionsProvider>

          {/* Products modal */}
          {modals.products && (
            <DetailPageModal
              title={t("Add-ons")}
              isOpen={modals.products.isOpen}
              onClose={modals.products.onClose}
              venue={rawData.event?.venue}
              itemListElement={
                <ProductCategoriesList
                  eventData={rawData.event}
                  productCategories={rawData.productCategories ?? []}
                  isLoading={data.ui.isLoading}
                />
              }
            />
          )}
        </>
      )}

      {/* Membership-specific modals */}
      {pageType === "membership" &&
        modals.memberships &&
        rawData.membership && (
          <DetailPageModal
            title={t("Memberships")}
            isOpen={modals.memberships.isOpen}
            onClose={modals.memberships.onClose}
            venue={rawData.membership?.venue}
            itemListElement={
              <MembershipTypesList
                membershipGroup={rawData.membership}
                membershipLevels={rawData.membership?.membership_levels ?? []}
                venue={rawData.membership?.venue}
                currency={rawData.membership?.venue?.currency}
                hasAllInPricing={Boolean(
                  rawData.membership?.venue?.all_in_pricing_is_enabled
                )}
                isLoading={data.ui.isLoading}
              />
            }
          />
        )}
    </>
  );
}
